import axios from 'axios'
import { t } from '@/utils/i18nHelper'
import { translateApiMessage } from '@/utils/apiMessageMap'
import { resolveAppPath } from '@/utils/publicPath'
import {
  clearAuthTokens,
  getAccessToken,
  getRefreshToken,
  setAuthTokens,
  shouldAttemptSilentRefresh,
  usesHttpOnlyRefresh,
} from '@/utils/authTokens'
import { setSessionMeta, touchSessionActivity } from '@/utils/sessionConfig'

/** Evita loop: interceptador não deve sair da página de licença inválida */
function isLicenseInvalidRoute() {
  try {
    return window.location.pathname.includes('license-invalid')
  } catch {
    return false
  }
}

/** Base da API compatível com deploy na raiz ou em subpasta */
const normalizedBaseUrl = (() => {
  const appBase = import.meta.env.BASE_URL || '/'
  const fromEnv = import.meta.env.VITE_API_BASE_URL?.trim()

  if (fromEnv) {
    const cleaned = fromEnv.replace(/\/$/, '')
    if (/^https?:\/\//i.test(cleaned)) {
      return cleaned
    }
    if (cleaned.startsWith('/') && !cleaned.startsWith('//')) {
      if (appBase === './' || appBase.startsWith('./')) {
        return `.${cleaned}`
      }
      if (appBase && appBase !== '/' && !appBase.startsWith('.')) {
        const pb = appBase.endsWith('/') ? appBase.slice(0, -1) : appBase
        return `${pb}${cleaned}`.replace(/\/{2,}/g, '/')
      }
      return cleaned
    }
    return cleaned
  }

  if (appBase === './' || appBase.startsWith('./')) {
    return './api'
  }
  const prefix = appBase.endsWith('/') ? appBase : `${appBase}/`
  if (prefix === '/') return '/api'
  return `${prefix}api`.replace(/\/{2,}/g, '/')
})()

// Configuração base do axios
const api = axios.create({
  baseURL: normalizedBaseUrl,
  timeout: 10000,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

let refreshPromise = null

function redirectToLogin() {
  if (
    !window.location.pathname.includes('install') &&
    !window.location.pathname.includes('/auth/login') &&
    !isLicenseInvalidRoute()
  ) {
    window.location.href = resolveAppPath('auth/login')
  }
}

async function refreshAccessToken(options = {}) {
  const { touchActivity = false } = options
  const httpOnly = usesHttpOnlyRefresh()
  const refreshToken = getRefreshToken()
  if (!httpOnly && !refreshToken) {
    return null
  }

  if (!refreshPromise) {
    const refreshBody = httpOnly ? {} : { refresh_token: refreshToken }
    refreshPromise = axios
      .post(
        `${normalizedBaseUrl.replace(/\/$/, '')}/auth/refresh`,
        refreshBody,
        {
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          timeout: 15000,
          withCredentials: httpOnly,
        }
      )
      .then((response) => {
        const body = response.data
        if (!body?.success) {
          throw new Error(body?.message || 'Refresh failed')
        }
        const data = body.data || {}
        const accessToken = data.access_token || data.token
        if (!accessToken) {
          throw new Error('Refresh response missing access token')
        }
        setAuthTokens({
          accessToken,
          refreshToken: data.refresh_token || refreshToken,
          refreshTokenDelivery: data.refresh_token_delivery,
        })
        if (data.session) {
          setSessionMeta(data.session)
        } else if (data.expires_in) {
          setSessionMeta({
            access_token_ttl_seconds: data.expires_in,
          })
        }
        if (touchActivity) {
          touchSessionActivity()
        }
        return accessToken
      })
      .finally(() => {
        refreshPromise = null
      })
  }

  return refreshPromise
}

async function logoutOnServer(payload = {}) {
  const httpOnly = usesHttpOnlyRefresh()
  const body =
    !httpOnly && payload.refresh_token ? { refresh_token: payload.refresh_token } : {}
  const url = `${normalizedBaseUrl.replace(/\/$/, '')}/auth/logout`
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' }

  for (const method of ['post', 'delete']) {
    try {
      await axios({
        method,
        url,
        data: Object.keys(body).length ? body : undefined,
        headers,
        timeout: 10000,
        withCredentials: httpOnly,
      })
      return
    } catch (error) {
      if (error.response?.status === 404) {
        continue
      }
      return
    }
  }
}

// Interceptador de request
api.interceptors.request.use(
  (config) => {
    config.withCredentials = usesHttpOnlyRefresh()

    // Adiciona token de autenticação se existir
    const token = getAccessToken()
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  (error) => {
    console.error('❌ Request Error:', error)
    return Promise.reject(error)
  }
)

// Interceptador de response
api.interceptors.response.use(
  (response) => {
    // Verifica se a resposta tem o formato esperado
    if (response.data && typeof response.data === 'object') {
      // Se tem propriedade success, usa o padrão da API
      if (response.data.hasOwnProperty('success')) {
        const allowSoftFailure = response.config?._allowSoftFailure === true
        if (!response.data.success && response.data.message && !allowSoftFailure) {
          // Erro da API mesmo com status 200
          throw new APIError(response.data.message, response.data.errors || {})
        }
        return response.data
      }
    }

    return response.data
  },
  async (error) => {
    const originalConfig = error.config
    const isSilent = originalConfig?._silentError === true

    if (!isSilent) {
      console.error('❌ Response Error:', error)
    }

    // Tratamento de erros HTTP
    if (error.response) {
      const { status, data } = error.response

      // Erro 401 - tenta refresh silencioso antes de deslogar
      if (status === 401) {
        const requestUrl = originalConfig?.url || ''
        const isLoginRequest = requestUrl.includes('/auth/login')
        const isRefreshRequest = requestUrl.includes('/auth/refresh')
        const alreadyRetried = originalConfig?._authRetry

        if (
          !isLoginRequest &&
          !isRefreshRequest &&
          !alreadyRetried &&
          shouldAttemptSilentRefresh()
        ) {
          try {
            const newToken = await refreshAccessToken()
            if (newToken && originalConfig) {
              originalConfig._authRetry = true
              originalConfig.headers = originalConfig.headers || {}
              originalConfig.headers.Authorization = `Bearer ${newToken}`
              return api(originalConfig)
            }
          } catch (refreshError) {
            console.warn('⚠️ [API] Silent refresh failed:', refreshError?.message || refreshError)
          }
        }

        console.error('🚫 [API] 401 Unauthorized recebido!', {
          url: requestUrl,
          hadToken: !!originalConfig?.headers?.Authorization,
          currentPath: window.location.pathname
        })

        if (!isLoginRequest && !isRefreshRequest) {
          clearAuthTokens()
          redirectToLogin()
        }

        const message = translateApiMessage(data?.message) || t('errors.unauthorized')
        throw new APIError(message, { auth: true })
      }

      // Erro 403 — licença inválida / suspensa: levar o utilizador à SPA de licença (evita painel “meio carregado”)
      if (status === 403 && data?.error_type === 'license_invalid') {
        if (!isLicenseInvalidRoute()) {
          const msg = data?.message ? String(data.message) : ''
          const sup = data?.support_url
            ? String(data.support_url)
                .replace(/\/v1\/license\/validate\/?$/i, '')
                .replace(/\/+$/, '')
            : ''
          const code = data?.license_code ? String(data.license_code) : ''
          const domain = data?.current_domain ? String(data.current_domain) : ''
          const q = new URLSearchParams()
          if (msg) q.set('error', msg)
          if (code) q.set('code', code)
          if (domain) q.set('domain', domain)
          if (sup) q.set('support_url', sup)
          const suffix = q.toString() ? `?${q.toString()}` : ''
          window.location.replace(resolveAppPath(`license-invalid${suffix}`))
        }
        throw new APIError(translateApiMessage(data?.message) || t('errors.forbidden'), { licenseInvalid: true })
      }

      // Erro 403 - Sem permissão (preserva mensagem da API se disponível)
      if (status === 403) {
        const msg403 = translateApiMessage(data?.message) || t('errors.forbidden')
        throw new APIError(msg403, { permission: true })
      }

      // Erro 404 - Endpoint não encontrado
      if (status === 404) {
        throw new APIError(translateApiMessage(data?.message) || t('errors.notFound'), { notFound: true })
      }

      // Erro 429 - Rate limit (muitas tentativas)
      if (status === 429) {
        const msg429 = translateApiMessage(data?.message) || t('auth.tooManyAttempts')
        throw new APIError(msg429, { rateLimit: true, retry_after: data?.retry_after })
      }

      // Erro 422 - Validação
      if (status === 422 && data.errors) {
        throw new ValidationError(t('errors.badRequest'), data.errors)
      }

      // Erro 500 - Servidor (preserva message_key e output para instalação API)
      if (status >= 500) {
        const message = translateApiMessage(data?.message) || t('errors.serverError')
        const apiError = new APIError(message, data || { server: true })
        apiError.response = error.response
        throw apiError
      }

      // Outros erros HTTP — preserva resposta completa para diagnóstico
      const message = translateApiMessage(data?.message) || `${t('errors.generic')} (HTTP ${status})`
      const apiError = new APIError(message, data || {})
      apiError.response = error.response
      throw apiError
    }

    // Timeout do axios (restore ainda pode ter concluído no servidor — use o fluxo upload+run+status)
    if (error.code === 'ECONNABORTED') {
      throw new APIError(t('errors.timeout'), { timeout: true })
    }

    // Erro de rede (502/504 Nginx, ligação cortada, etc.)
    if (error.request) {
      const status = error.response?.status
      const hint =
        status === 502 || status === 504
          ? ' Verifique fastcgi_read_timeout e request_terminate_timeout (docs/BACKUP_RESTORE_NGINX.md).'
          : ''
      throw new APIError(t('errors.networkError') + hint, { network: true })
    }

    // Outros erros
    throw new APIError(t('errors.generic'), { unknown: true })
  }
)

// Classes de erro customizadas
export class APIError extends Error {
  constructor(message, details = {}) {
    super(message)
    this.name = 'APIError'
    this.details = details
  }
}

export class ValidationError extends APIError {
  constructor(message, errors = {}) {
    super(message)
    this.name = 'ValidationError'
    this.errors = errors
  }
}

// Métodos de conveniência
export const apiService = {
  // Instalação
  install: {
    check: () => api.get('/install/check'),
    // Schema + seeds no servidor podem demorar muito mais que o timeout global (10s)
    testConnection: (data) =>
      api.post('/install/test-connection', data, { timeout: 120000 }),
    execute: (data) => api.post('/install/execute', data, { timeout: 300000 })
  },

  license: {
    status: (options = {}) => {
      const force = options?.force === true
      return api.get('/license/status', {
        ...(force ? { params: { force: '1' } } : {}),
        _silentError: options?.silent === true,
      })
    },
  },

  // Autenticação
  auth: {
    login: (credentials) => api.post('/auth/login', credentials),
    logout: (payload = {}) => logoutOnServer(payload),
    refresh: (payload = {}) => api.post('/auth/refresh', payload),
    me: () => api.get('/auth/me'),
    impersonate: (userId) => api.post('/auth/impersonate', { user_id: userId }),
    stopImpersonate: (adminToken) => api.post('/auth/stop-impersonate', { admin_token: adminToken })
  },

  // Usuários
  users: {
    list: (params = {}) => api.get('/users', { params }),
    get: (id) => api.get(`/users/${id}`),
    create: (data) => api.post('/users', data),
    update: (id, data) => api.put(`/users/${id}`, data),
    delete: (id) => api.delete(`/users/${id}`),
    suspend: (id) => api.patch(`/users/${id}/suspend`),
    activate: (id) => api.patch(`/users/${id}/activate`)
  },

  // Categorias
  categories: {
    list: () => api.get('/categories'),
    get: (id) => api.get(`/categories/${id}`),
    create: (data) => api.post('/categories', data),
    update: (id, data) => api.put(`/categories/${id}`, data),
    delete: (id) => api.delete(`/categories/${id}`),
    setDefault: (id) => api.post(`/categories/${id}/set-default`),
    migrate: (data) => api.post('/categories/migrate', data)
  },

  // Revendedores (bloco consolidado)
  resellers: {
    list: (params = {}) => api.get('/resellers', { params }),
    getCreators: () => api.get('/resellers/creators'),
    get: (id) => api.get(`/resellers/${id}`),
    show: (id) => api.get(`/resellers/${id}`),
    getCurrent: () => api.get('/resellers/current'),
    create: (data) => api.post('/resellers', data),
    createFromPlan: (data) => api.post('/resellers/from-plan', data),
    update: (id, data) => api.put(`/resellers/${id}`, data),
    delete: (id) => api.delete(`/resellers/${id}`),
    deleteMultiple: (ids) => api.post('/resellers/delete-multiple', { ids }),
    suspend: (id) => api.patch(`/resellers/${id}/suspend`),
    activate: (id) => api.patch(`/resellers/${id}/activate`),
    addCredits: (id, data) => api.post(`/resellers/${id}/add-credits`, data),
    renewSubscribe: (id, data) => api.post(`/resellers/${id}/renew`, data),
    renewalPlans: () => api.get('/resellers/renewal-plans'),
    renewalPaymentMethods: () => api.get('/resellers/renewal-payment-methods'),
    createRenewalPayment: (data) => api.post('/resellers/renewal-payment', data),
    creditPlans: () => api.get('/resellers/credit-plans'),
    createCreditPayment: (data) => api.post('/resellers/credit-payment', data),
    stats: (id) => api.get(`/resellers/${id}/stats`),
    users: (id) => api.get(`/resellers/${id}/users`),
    toggleStatus: (id) => api.patch(`/resellers/${id}/toggle-status`)
  },

  // Servidores
  servers: {
    list: (params = {}) => api.get('/servers', { params }),
    get: (id) => api.get(`/servers/${id}`),
    create: (data) => api.post('/servers', data),
    update: (id, data) => api.put(`/servers/${id}`, { ...data, id }), // Inclui ID no body também
    delete: (id) => api.delete(`/servers/${id}`),
    test: (id) => api.post(`/servers/${id}/test`),
    stats: (id) => api.get(`/servers/${id}/stats`),
    /** Só cria o job — processamento em background (resposta rápida). */
    sync: (id) => api.post(`/servers/${id}/sync`, {}, { timeout: 60000 }),
    syncStatus: (jobId) => api.get('/servers/sync-status', { params: { job_id: jobId } }),
    // Gerenciamento de API
    installApi: (id, data) => api.post(`/servers/${id}/install-api`, { ...data, server_id: id }, { timeout: 300000 }), // 5 min - operação SSH longa
    resetInstallProgress: (id) => api.post(`/servers/${id}/reset-install-progress`, { server_id: id }),
    uninstallApi: (id) => api.post(`/servers/${id}/uninstall-api`, { server_id: id }, { timeout: 60000 }), // 1 min - SSH
    testSsh: (id) => api.post(`/servers/${id}/test-ssh`, { server_id: id }, { timeout: 30000 }), // 30s - teste de conectividade
    apiStatus: (id) =>
      api.get(`/servers/${id}/api-status?server_id=${id}`, {
        _allowSoftFailure: true,
        _silentError: true,
      }),
    installationStatus: (serverId) => api.get(`/servers/installation-status?server_id=${serverId}`),
    // Métricas em tempo real (proxy para API remota) — falha remota = offline, sem toast global
    onlineCount: (id) =>
      api.get(`/servers/${id}/online-count`, {
        _allowSoftFailure: true,
        _silentError: true,
      }),
    onlineUsers: (id) =>
      api.get(`/servers/${id}/online-users`, {
        _allowSoftFailure: true,
        _silentError: true,
      }),
    systemResources: (id) =>
      api.get(`/servers/${id}/system-resources`, {
        _allowSoftFailure: true,
        _silentError: true,
      }),
  },

  // Sistema
  system: {
    health: () => api.get('/health'),
    status: () => api.get('/status'),
    info: () => api.get('/api-info'),
    stats: () => api.get('/system/stats'),
    logs: (params = {}) => api.get('/system/logs', { params })
  },

  // Logs do Sistema
  logs: {
    list: (params = {}) => api.get('/logs', { params }),
    stats: () => api.get('/logs/stats'),
    clearAll: () => api.delete('/logs'),
  },

  // Monitoramento
  monitoring: {
    dashboardStats: () => api.get('/dashboard/stats'),
    onlineUsers: () => api.get('/monitoring/online')
  },

    // Pagamentos
  payment: {
    // Configuração
    getConfig: () => api.get('/payment/config'),
    updateConfig: (data) => api.put('/payment/config', data),
    
    // Métodos de pagamento
    getMethods: () => api.get('/payment/methods'),
    updateMethods: (data) => api.put('/payment/methods', data),
    
    // Criar pagamento
    create: (data) => api.post('/payment/create', data),
    
    // Listagem e consulta
    list: (params = {}) => api.get('/payment/list', { params }),
    get: (id) => api.get(`/payment/${id}`),
    checkStatus: (paymentId) => api.get(`/payment/check/${paymentId}`),
    
    // Métricas
    metrics: (params = {}) => api.get('/payment/metrics', { params }),
    
    // Ações
    cancel: (id) => api.post(`/payment/${id}/cancel`),
    approveManual: (id) => api.post(`/payment/${id}/approve-manual`),
    refund: (id) => api.post(`/payment/${id}/refund`)
  },

  // Cupons
  coupons: {
    list: () => api.get('/coupons'),
    create: (data) => api.post('/coupons', data),
    update: (id, data) => api.put(`/coupons/${id}`, data),
    delete: (id) => api.delete(`/coupons/${id}`),
    validate: (data) => api.post('/coupons/validate', data)
  },

  // Dispositivos
  devices: {
    list: (params = {}) => api.get('/devices', { params }),
    remove: (id) => api.delete(`/devices/${id}`),
    clean: (data) => api.post('/devices/clean', data),
    stats: () => api.get('/devices/stats')
  },

  // Configurações do Painel (Super Admin)
  panelSettings: {
    getAll: () => api.get('/panel-settings'),
    getPublic: () => api.get('/panel-settings/public'),
    getByKey: (key) => api.get(`/panel-settings/key/${key}`),
    update: (id, data) => api.put(`/panel-settings/${id}`, data),
    updateByKey: (key, data) => api.patch(`/panel-settings/key/${key}`, data),
    uploadFile: async (settingKey, file) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('setting_key', settingKey)
      
      return api.post('/panel-settings/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
    }
  },
  
  // ✅ NOVO: Settings específicos
  settings: {
    getResellerSettings: () => api.get('/settings/reseller'),
    updateResellerSetting: (key, data) => api.put(`/settings/reseller/${key}`, data)
  },

  // Gerenciamento de Administradores (Super Admin)
  adminManagement: {
    list: () => api.get('/admin-management'),
    create: (data) => api.post('/admin-management', data),
    update: (id, data) => api.put(`/admin-management/${id}`, data),
    delete: (id) => api.delete(`/admin-management/${id}`),
    toggleStatus: (id) => api.patch(`/admin-management/${id}/toggle-status`)
  },
  
  // Perfil do Admin
  admin: {
    updateProfile: (data) => api.put('/admin/profile', data),
    uploadAvatar: (formData) => api.post('/admin/profile/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    deleteAvatar: () => api.delete('/admin/profile/avatar')
  },

  // Portal do Revendedor (reseller logado)
  reseller: {
    updateProfile: (data) => api.put('/reseller/profile', data),
    getDashboardStats: () => api.get('/reseller/stats')
  },
  
  // Contas (SSH, V2Ray, Combos)
  accounts: {
    list: (params) => api.get('/accounts', { params }),
    get: (id) => api.get(`/accounts/${id}`),
    getCreators: () => api.get('/accounts/creators'),
    create: (data) => api.post('/accounts', data),
    createFromPlan: (data) => api.post('/accounts/from-plan', data),
    update: (id, data) => api.put(`/accounts/${id}`, data),
    toggleStatus: (id) => api.patch(`/accounts/${id}/toggle-status`),
    delete: (id) => api.delete(`/accounts/${id}`),
    deleteMultiple: (ids) => api.post('/accounts/delete-multiple', { ids }),
    renew: (id, data) => api.patch(`/accounts/${id}/renew`, data)
  },

  jobs: {
    deletionStatus: (jobId) =>
      api.get('/jobs/deletion/status', { params: { job_id: jobId }, timeout: 15000 }),
  },

  // Planos de Contas
  accountPlans: {
    list: () => api.get('/account-plans'),
    create: (data) => api.post('/account-plans', data),
    update: (id, data) => api.put(`/account-plans/${id}`, data),
    delete: (id) => api.delete(`/account-plans/${id}`)
  },

  // Planos de Revenda
  resellerPlans: {
    list: () => api.get('/reseller-plans'),
    create: (data) => api.post('/reseller-plans', data),
    update: (id, data) => api.put(`/reseller-plans/${id}`, data),
    delete: (id) => api.delete(`/reseller-plans/${id}`)
  },
  
  // Contas de Teste
  testAccounts: {
    create: (data) => api.post('/test-accounts/create', data)
  },

  // Automações
  automations: {
    list: () => api.get('/automations'),
    toggleFeature: (feature, enabled, extraData = {}) => api.put(`/automations/${feature}/toggle`, { 
      enabled,
      ...extraData
    }),
    update: (type, config) => api.put(`/automations/${type}`, config),
    savePaymentConfig: (config) => api.put('/automations/payment-config', config),
    testMercadoPago: (token) => api.post('/automations/test-mercadopago', { token }),
    
    // Multi-Gateway
    listPaymentGateways: () => api.get('/automations/payment-gateways'),
    savePaymentGateway: (gateway) => api.post('/automations/payment-gateway', gateway),
    deletePaymentGateway: (id) => api.delete(`/automations/payment-gateway/${id}`),
    
    // ✅ NOVO: Métodos de pagamento
    getRegionPaymentMethods: (provider, region) => api.get(`/automations/payment-methods/preview/${provider}/${region}`),
    getAvailablePaymentMethods: (gatewayId) => api.get(`/automations/payment-methods/${gatewayId}`),
    updatePaymentMethods: (gatewayId, methods) => api.put(`/automations/payment-gateway/${gatewayId}/methods`, { selected_methods: methods }),
    
    // Backup
    triggerBackup: () => api.post('/automations/backup/trigger', null, { timeout: 120000 })
  },

  // Backup/Restore
  backup: {
    /** mysqldump + Telegram pode ultrapassar o timeout global (10s). */
    create: () => api.post('/settings/backup/create', null, { timeout: 120000 }),

    getStatus: (jobId) => api.get(`/settings/backup/status/${jobId}`, { timeout: 30000 }),

    waitForJob: async (jobId) => {
      const deadline = Date.now() + 900000
      const pollIntervalMs = 2000

      while (Date.now() < deadline) {
        const res = await api.get(`/settings/backup/status/${jobId}`, { timeout: 30000 })
        const st = res.status
        const state = st?.state

        if (state === 'completed') {
          return {
            success: true,
            message: st.message,
            data: st,
          }
        }
        if (state === 'failed') {
          throw new APIError(st?.message || t('errors.generic'))
        }

        await new Promise((resolve) => setTimeout(resolve, pollIntervalMs))
      }

      throw new APIError(t('errors.timeout'), { timeout: true })
    },

    createAndWait: async () => {
      const res = await api.post('/settings/backup/create', null, { timeout: 120000 })
      if (!res.job_id) {
        throw new APIError(res.message || t('errors.generic'))
      }
      return apiService.backup.waitForJob(res.job_id)
    },

    triggerAndWait: async () => {
      const res = await api.post('/automations/backup/trigger', null, { timeout: 120000 })
      if (!res.job_id) {
        throw new APIError(res.message || t('errors.generic'))
      }
      return apiService.backup.waitForJob(res.job_id)
    },
    /**
     * Restauração em 3 passos (produção com Nginx/Cloudflare):
     * 1) upload do .sql/.enc
     * 2) run em background (resposta HTTP imediata)
     * 3) poll de status (pedidos curtos — evita timeout com import longo)
     */
    restore: async (formData) => {
      const password = formData.get('password')
      if (password !== null && password !== undefined && password !== '') {
        formData.delete('password')
      }

      const uploadRes = await api.post('/settings/backup/restore/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 900000,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      })

      const jobId = uploadRes.job_id
      if (!jobId) {
        throw new APIError(uploadRes.message || t('errors.generic'))
      }

      await api.post(
        '/settings/backup/restore/run',
        { job_id: jobId, password: password || undefined },
        { timeout: 120000 }
      )

      const deadline = Date.now() + 900000
      const pollIntervalMs = 2000

      while (Date.now() < deadline) {
        const st = await api.get(`/settings/backup/restore/status/${jobId}`, {
          timeout: 30000,
        })

        const state = st.state
        if (state === 'completed') {
          return {
            success: true,
            message: st.message,
            data: st.data,
          }
        }
        if (state === 'failed') {
          throw new APIError(st.message || t('errors.generic'))
        }

        await new Promise((resolve) => setTimeout(resolve, pollIntervalMs))
      }

      throw new APIError(t('errors.timeout'), { timeout: true })
    },
  },

  // CheckUser
  checkuser: {
    stats: () => api.get('/checkuser/stats'),
    getConfig: () => api.get('/checkuser/config'),
    updateConfig: (config) => api.put('/checkuser/config', config),
    clearUserDevices: (userId) => api.delete(`/checkuser/devices/${userId}`),
    clearAllDevices: () => api.delete('/checkuser/devices/all'),
    runCleanup: () => api.post('/checkuser/run-cleanup')
  },

  // Logs do Sistema
  logs: {
    list: (params = {}) => api.get('/logs', { params }),
    get: (id) => api.get(`/logs/${id}`),
    stats: () => api.get('/logs/stats'),
    actions: () => api.get('/logs/actions'),
    clearAll: () => api.delete('/logs'),
  },

  // Branding (Logos, Ícones, Favicon)
  branding: {
    get: () => api.get('/branding'),
    getAdminConfig: () => api.get('/branding/admin-config'),
    upload: (formData) => api.post('/branding/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }),
    updateSettings: (settings) => api.put('/branding/settings', settings),
    delete: (type) => api.delete(`/branding/${type}`)
  },

  messageTemplates: {
    get: () => api.get('/message-templates'),
    update: (payload) => api.put('/message-templates', payload),
    clearOverride: (key) => api.delete(`/message-templates/${encodeURIComponent(key)}`),
  },

  // APIs Públicas (sem autenticação)
  public: {
    // Vendas de Contas
    getAccountPlans: (slug) => api.get(`/public/sales/${slug}/plans`),
    getPaymentMethods: (slug) => api.get(`/public/sales/${slug}/payment-methods`),
    createAccountPayment: (slug, data) => api.post(`/public/sales/${slug}/payment`, data),
    
    // Vendas de Revenda
    getResellerPlans: (slug) => api.get(`/public/reseller-sales/${slug}/plans`),
    getResellerPaymentMethods: (slug) => api.get(`/public/reseller-sales/${slug}/payment-methods`),
    createResellerPayment: (slug, data) => api.post(`/public/reseller-sales/${slug}/payment`, data),
    checkResellerUsername: (username) => api.get('/public/reseller-sales/check-username', { params: { username } }),
    checkResellerEmail: (email) => api.get('/public/reseller-sales/check-email', { params: { email } }),
    
    // Status de Pagamento
    checkPayment: (paymentId) => api.get(`/public/payment/${paymentId}/status`, { timeout: 30000 }),
    getSellerMessageTemplates: (slug) => api.get(`/public/sales/${slug}/message-templates`),
    getSellerMessageTemplates: (slug) => api.get(`/public/sales/${slug}/message-templates`),
    
    // Cancelar Pagamento
    cancelPayment: (paymentId) => api.post(`/public/payment/${paymentId}/cancel`)
  },
  
  // APIs de Renovação (sem autenticação)
  renewal: {
    searchAccount: (identifier) => api.post('/public/renewal/search', { identifier }),
    getPlans: (sellerId, categoryId, type) =>
      api.get('/public/renewal/plans', {
        params: { seller_id: sellerId, category_id: categoryId, type },
      }),
    getPaymentMethods: (sellerId) => api.get('/public/renewal/payment-methods', { params: { seller_id: sellerId } }),
    createPayment: (data) => api.post('/public/renewal/payment', data)
  }
}

export { api, refreshAccessToken }
export default apiService